import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import Reveal from '@/components/Reveal';

/**
 * VideoFeature — full-bleed editorial video block with eyebrow + title + custom play controls.
 *
 * Props:
 *  - eyebrow, title, subtitle
 *  - src: mp4 url
 *  - poster: poster image url
 *  - tone: 'forest' | 'ink' | 'orange' | 'red' (affects overlay tint)
 *  - autoplay: bool (default true, muted+loop)
 *  - testId
 */
export default function VideoFeature({
  eyebrow,
  title,
  subtitle,
  src,
  poster,
  tone = 'forest',
  autoplay = true,
  testId = 'video-feature',
}) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(autoplay);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (autoplay) {
      // Best-effort autoplay (must be muted)
      v.muted = true;
      v.play().catch(() => setPlaying(false));
    }
  }, [autoplay]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const overlayGrad = {
    forest: 'bg-gradient-to-br from-[hsl(var(--c-forest-deep)/0.70)] via-[hsl(var(--c-forest-deep)/0.35)] to-[hsl(var(--c-forest-deep)/0.55)]',
    ink:    'bg-gradient-to-br from-[hsl(var(--c-ink)/0.65)] via-[hsl(var(--c-ink)/0.30)] to-[hsl(var(--c-ink)/0.55)]',
    orange: 'bg-gradient-to-br from-[hsl(var(--c-mango-orange-deep)/0.55)] via-transparent to-[hsl(var(--c-mango-orange-deep)/0.45)]',
    red:    'bg-gradient-to-br from-[hsl(var(--c-mango-red-deep)/0.55)] via-transparent to-[hsl(var(--c-mango-red-deep)/0.45)]',
  }[tone] || 'bg-gradient-to-br from-[hsl(var(--c-forest-deep)/0.70)] via-[hsl(var(--c-forest-deep)/0.35)] to-[hsl(var(--c-forest-deep)/0.55)]';

  return (
    <section
      data-testid={testId}
      className="relative overflow-hidden border-t-2 border-b-2 border-[hsl(var(--c-ink))] min-h-[480px] sm:min-h-[560px] lg:min-h-[640px]"
    >
      {/* Video layer */}
      <video
        ref={ref}
        src={src}
        poster={poster}
        autoPlay={autoplay}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 h-full w-full object-cover"
        aria-label={typeof title === 'string' ? title : 'Florida mangoes feature video'}
      />

      {/* Overlay tint + grain */}
      <div className={`absolute inset-0 ${overlayGrad}`} aria-hidden="true" />
      <div className="absolute inset-0 grain" aria-hidden="true" />

      {/* Accent stripes */}
      <div className="absolute top-0 left-0 right-0 h-[6px] bg-[hsl(var(--c-mango-yellow))]" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 right-0 h-[6px] bg-[hsl(var(--c-mango-orange))]" aria-hidden="true" />

      {/* Content */}
      <div className="relative mx-auto max-w-[1400px] h-full px-5 sm:px-10 lg:px-16 py-20 sm:py-24 lg:py-32 min-h-[480px] sm:min-h-[560px] lg:min-h-[640px] flex flex-col justify-end">
        <Reveal>
          <div className="max-w-3xl text-white">
            {eyebrow && <div className="eyebrow text-white/95 drop-shadow-[0_1px_0_rgba(0,0,0,0.4)]">{eyebrow}</div>}
            {title && (
              <h2 className="mt-3 font-heading display-lg font-extrabold leading-[0.98] drop-shadow-[0_2px_0_rgba(0,0,0,0.35)]" style={{ color: '#fff' }}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-white/90 drop-shadow-[0_1px_0_rgba(0,0,0,0.4)]">
                {subtitle}
              </p>
            )}
          </div>
        </Reveal>

        {/* Custom controls */}
        <div className="mt-8 flex items-center gap-0">
          <button
            type="button"
            onClick={toggle}
            data-testid={`${testId}-play`}
            aria-label={playing ? 'Pause video' : 'Play video'}
            className="btn-square btn-transition inline-flex items-center justify-center h-12 sm:h-14 px-5 sm:px-7 text-xs sm:text-sm font-bold uppercase tracking-wider border-2 border-[hsl(var(--c-ink))] bg-grad-mango text-white"
          >
            {playing ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {playing ? 'Pause' : 'Play'}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            data-testid={`${testId}-mute`}
            aria-label={muted ? 'Unmute video' : 'Mute video'}
            className="btn-square btn-transition inline-flex items-center justify-center h-12 sm:h-14 px-5 sm:px-7 text-xs sm:text-sm font-bold uppercase tracking-wider border-2 border-[hsl(var(--c-ink))] bg-white text-[hsl(var(--c-ink))] -ml-[2px]"
          >
            {muted ? <VolumeX className="h-4 w-4 mr-2" /> : <Volume2 className="h-4 w-4 mr-2" />}
            {muted ? 'Unmute' : 'Mute'}
          </button>
        </div>
      </div>
    </section>
  );
}
